import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Download,
  Calendar,
  Building2,
  Receipt,
  ArrowRightLeft,
  Search,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { afipIvaIdFromPercent } from '../utils/afipIva';

export default function ReportesImpositivos() {
  const { profile } = useAuth();
  
  const [activeTab, setActiveTab] = useState('ventas'); // ventas | compras | ret_sufridas | ret_practicadas
  
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [loading, setLoading] = useState(false);
  const [datosVentas, setDatosVentas] = useState([]);
  const [datosCompras, setDatosCompras] = useState([]);
  const [retSufridas, setRetSufridas] = useState([]);
  const [retPracticadas, setRetPracticadas] = useState([]);

  // El Libro IVA Ventas es POR CUIT: hay que poder filtrar por emisor para no
  // mezclar varios CUITs en un mismo archivo.
  const [billingEntities, setBillingEntities] = useState([]);
  const [emisorSel, setEmisorSel] = useState('');

  // CUIT de cada proveedor (por id) para el Libro IVA Compras.
  const [proveedoresMap, setProveedoresMap] = useState({});

  useEffect(() => {
    if (!profile?.organization_id) return;
    supabase
      .from('billing_entities')
      .select('id, tax_name, cuit, afip_pto_venta, fiscal_configuration')
      .eq('organization_id', profile.organization_id)
      .then(({ data }) => {
        const list = data || [];
        setBillingEntities(list);
        if (list.length === 1) setEmisorSel(list[0].id);
      });

    supabase
      .from('proveedores')
      .select('id, cuit')
      .eq('organization_id', profile.organization_id)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((p) => { m[p.id] = p.cuit; });
        setProveedoresMap(m);
      });
  }, [profile]);

  useEffect(() => {
    if (profile?.organization_id) {
      cargarDatos();
    }
  }, [profile, fechaDesde, fechaHasta, activeTab, emisorSel]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const start = `${fechaDesde}T00:00:00.000Z`;
      const end = `${fechaHasta}T23:59:59.999Z`;
      
      if (activeTab === 'ventas') {
        let q = supabase
          .from('sales')
          .select('*, clients(full_name, doc_number, doc_type, tax_condition)')
          .eq('organization_id', profile.organization_id)
          .gte('fecha_facturacion', fechaDesde)
          .lte('fecha_facturacion', fechaHasta);
        // Libro IVA por CUIT: si hay un emisor seleccionado, filtramos por él.
        if (emisorSel) q = q.eq('billing_entity_id', emisorSel);
        const { data, error } = await q.order('fecha_facturacion', { ascending: true });
        if (error) throw error;
        setDatosVentas(data || []);
      }
      
      if (activeTab === 'compras') {
        const { data, error } = await supabase
          .from('compras')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .gte('fecha', start)
          .lte('fecha', end)
          .order('fecha', { ascending: true });
        if (error) throw error;
        setDatosCompras(data || []);
      }

      if (activeTab === 'ret_sufridas') {
        const { data, error } = await supabase
          .from('retenciones_sufridas')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .gte('fecha', start)
          .lte('fecha', end)
          .order('fecha', { ascending: false });
        // Ignoramos error si la tabla no existe aún
        if (!error) setRetSufridas(data || []);
      }

      if (activeTab === 'ret_practicadas') {
        const { data, error } = await supabase
          .from('retenciones_practicadas')
          .select('*')
          .eq('organization_id', profile.organization_id)
          .gte('fecha', start)
          .lte('fecha', end)
          .order('fecha', { ascending: false });
        if (!error) setRetPracticadas(data || []);
      }

    } catch (error) {
      console.error("Error cargando datos impositivos:", error);
      toast.error("Error obteniendo los registros. Verifique conexión.");
    } finally {
      setLoading(false);
    }
  };

  // --- FUNCIONES EXPORTACIÓN TXT AFIP ---
  const padRight = (str, len) => String(str).substring(0, len).padEnd(len, ' ');
  const padLeft = (str, len, char = '0') => String(str).substring(0, len).padStart(len, char);
  const formatAmount = (num, totalLen=15) => {
      const parts = Number(num).toFixed(2).split('.');
      return padLeft(parts[0] + parts[1], totalLen, '0');
  };

  const generarTxtVentas = () => {
    // El Libro IVA Ventas es por CUIT: si hay más de un emisor, exigimos elegir uno.
    if (billingEntities.length > 1 && !emisorSel) {
      return toast.error("Elegí un emisor: el Libro IVA Ventas es por CUIT y no se pueden mezclar.");
    }
    if (datosVentas.length === 0) return toast.error("No hay ventas para exportar");
    
    let txtComprobantes = "";
    let txtAlicuotas = "";
    let alicuotasCount = 0;

    datosVentas.forEach(v => {
      // Ignoramos ventas no facturadas
      if (v.invoice_status !== 'facturado') return;

      const fechaBase = v.fecha_facturacion || v.invoice_sent_at || v.created_at || null;
      const fecha = fechaBase ? String(fechaBase).split('T')[0].replace(/-/g, '') : '00000000';
      // Tipo de comprobante: el CÓDIGO numérico real (1/6/11...), no el texto.
      const cbteTipo = padLeft(v.invoice_cbte_tipo || 11, 3);
      // Punto de venta y número: salen del numero_factura "PPPP-NNNNNNNN".
      const partsNF = String(v.numero_factura || '').split('-');
      const ptoVtaRaw = (partsNF[0] || v.afip_pto_venta || '1').replace(/\D/g, '') || '1';
      const nroCompRaw = (partsNF[1] || partsNF[0] || '1').replace(/\D/g, '') || '1';
      const ptoVenta = padLeft(ptoVtaRaw, 5);
      const nroComprobante = padLeft(nroCompRaw, 20);

      // Receptor: si no hay documento es Consumidor Final sin identificar (cód 99).
      const docNumberRaw = String(v.clients?.doc_number || '').replace(/\D/g, '');
      let docTipo = '99';
      let docNro = padLeft('0', 20, '0');
      if (docNumberRaw && Number(docNumberRaw) > 0) {
        docTipo = v.clients?.doc_type === 'CUIT' ? '80' : '96';
        docNro = padLeft(docNumberRaw, 20, '0');
      }
      const clienteNombre = padRight(
        (docTipo === '99' ? 'CONSUMIDOR FINAL' : (v.clients?.full_name || 'CONSUMIDOR FINAL')).toUpperCase(),
        30,
      );

      const importeTotal = formatAmount(v.total);
      
      // Conceptos aproximados (AFIP pide Neto Gravado, No Gravado, Exento, IVA, etc)
      // Asumiremos 21% para RI y 0% para consumidor final facturado con C
      const esFacturaA_B = cbteTipo === '001' || cbteTipo === '006';
      
      let netoGravado = formatAmount(0);
      let iva = formatAmount(0);
      let noGravado = formatAmount(0);
      let opExentas = formatAmount(0);

      const totalVal = Number(v.total || 0);

      // Mapa Id alícuota AFIP -> código de 4 dígitos del aplicativo.
      const alicuotaCodeFromId = (id) => String(Number(id) || 5).padStart(4, '0');

      // Desglose real por alícuota guardado al emitir el CAE (alícuotas mixtas).
      const ivaBreakdown = Array.isArray(v.invoice_iva) ? v.invoice_iva : null;
      const netoSnapshot = v.invoice_imp_neto != null ? Number(v.invoice_imp_neto) : null;
      const ivaSnapshot = v.invoice_imp_iva != null ? Number(v.invoice_imp_iva) : null;
      const opExSnapshot = v.invoice_imp_opex != null ? Number(v.invoice_imp_opex) : null;

      // Cantidad de alícuotas realmente emitidas (campo 19) y código de operación
      // (campo 20). Si no se emite ninguna alícuota (C / totalmente exento) -> 0 y 'E'.
      let alicEmitidas = 0;
      let codigoOp = ' ';

      if (esFacturaA_B && ivaBreakdown && ivaBreakdown.length > 0) {
          // Un registro de alícuota por cada tasa gravada realmente facturada.
          let netoAcum = 0;
          let ivaAcum = 0;
          for (const ali of ivaBreakdown) {
              const baseAli = Number(ali?.BaseImp || 0);
              const ivaAli = Number(ali?.Importe || 0);
              if (baseAli <= 0 && ivaAli <= 0) continue;
              netoAcum += baseAli;
              ivaAcum += ivaAli;
              txtAlicuotas += cbteTipo + ptoVenta + nroComprobante + formatAmount(baseAli) + alicuotaCodeFromId(ali?.Id) + formatAmount(ivaAli) + "\r\n";
              alicuotasCount++;
              alicEmitidas++;
          }
          netoGravado = formatAmount(netoSnapshot != null ? netoSnapshot : netoAcum);
          iva = formatAmount(ivaSnapshot != null ? ivaSnapshot : ivaAcum);
          if (opExSnapshot) opExentas = formatAmount(opExSnapshot);
      } else if (esFacturaA_B && netoSnapshot != null && netoSnapshot <= 0 && opExSnapshot) {
          // A/B totalmente exenta (sin neto gravado): va a exentas, sin alícuota.
          opExentas = formatAmount(opExSnapshot);
      } else if (esFacturaA_B) {
          // Fallback ventas previas sin snapshot: aproximación al 21%.
          const base = netoSnapshot != null ? netoSnapshot : totalVal / 1.21;
          const ivaCalc = ivaSnapshot != null ? ivaSnapshot : (totalVal - base);
          netoGravado = formatAmount(base);
          iva = formatAmount(ivaCalc);
          if (opExSnapshot) opExentas = formatAmount(opExSnapshot);
          txtAlicuotas += cbteTipo + ptoVenta + nroComprobante + formatAmount(base) + '0005' + formatAmount(ivaCalc) + "\r\n";
          alicuotasCount++;
          alicEmitidas++;
      } else {
          // Comprobante C (monotributo) u operación sin IVA: operación exenta.
          opExentas = formatAmount(totalVal);
      }

      // Campo 19 (1 dígito) y campo 20: si no hubo alícuotas, código 'E' (exentas).
      if (alicEmitidas === 0) codigoOp = 'E';
      const cantAlicuotasField = padLeft(alicEmitidas, 1);

      // Estructura de registro Libro IVA Ventas (Aprox 260 chars dependiendo de la versión RG 3685)
      // Tipo (8), CBTE(3), PtoVta(5), Nro(20), NroHasta(20), DocTipo(2), DocNro(20), Nombre(30), Total(15), TotalNoGravado(15), NetoGravado(15), ImpLiquidado(15)...
      const registro = 
        fecha +          // 1 - 8: Fecha Comprobante (YYYYMMDD)
        cbteTipo +       // 9 - 11: Tipo Comprobante
        ptoVenta +       // 12 - 16: Punto Venta
        nroComprobante + // 17 - 36: Nro Comprobante
        nroComprobante + // 37 - 56: Nro Comprobante Hasta
        docTipo +        // 57 - 58: Código Documento Identificador
        docNro +         // 59 - 78: Número Documento Identificador
        clienteNombre +  // 79 - 108: Nombre/Razón Social
        importeTotal +   // 109 - 123: Importe Total Operación
        noGravado +      // 124 - 138: Importe Total de Conceptos que no integran el PNR
        padLeft('0', 15) + // 139 - 153: Percepción a NO categorizados
        opExentas +      // 154 - 168: Operaciones Exentas
        padLeft('0', 15) + // 169 - 183: Percepciones o Pago a Cuenta I.N.
        padLeft('0', 15) + // 184 - 198: Percepciones IB
        padLeft('0', 15) + // 199 - 213: Percepciones Imp. Municipales
        padLeft('0', 15) + // 214 - 228: Impuestos Internos
        'PES' +          // 229 - 231: Código de Moneda
        '0001000000' +   // 232 - 241: Tipo de Cambio
        cantAlicuotasField + // 242 - 242: Cantidad de alícuotas IVA (real; 0 en C/exento)
        codigoOp +       // 243 - 243: Código de operación ('E' si 0 alícuotas)
        padLeft('0', 15) + // 244 - 258: Otros Tributos
        fecha;           // 259 - 266: Fecha Vencimiento (usamos misma fecha)

      txtComprobantes += registro + "\r\n";
    });

    // Descarga de Comprobantes
    downloadFile(txtComprobantes, `Ventas_Comprobantes_${fechaDesde.replace(/-/g,'')}_al_${fechaHasta.replace(/-/g,'')}.txt`);
    
    // Descarga de Alícuotas (si corresponde)
    if (alicuotasCount > 0) {
        setTimeout(() => {
            downloadFile(txtAlicuotas, `Ventas_Alicuotas_${fechaDesde.replace(/-/g,'')}_al_${fechaHasta.replace(/-/g,'')}.txt`);
        }, 500);
    }
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generarTxtCompras = () => {
    if (datosCompras.length === 0) return toast.error("No hay compras para exportar");

    let txtComprobantes = "";
    let txtAlicuotas = "";
    let alicuotasCount = 0;

    datosCompras.forEach(c => {
      const fecha = c.fecha ? c.fecha.split('T')[0].replace(/-/g, '') : '00000000';
      
      // Extraemos TIPO y NRO (EJ: "Factura A", "Nota Crédito B")
      const tipoLabel = (c.tipo_comprobante || 'Factura C').toUpperCase();
      let cbteRef = '011'; // C
      if (tipoLabel.includes(' A')) cbteRef = '001';
      if (tipoLabel.includes(' B')) cbteRef = '006';
      
      const cbteTipo = cbteRef;

      // Dividimos "00001-00000023" si lo ingresaron así, sino default.
      let pVta = '1';
      let numComp = '1';
      const nroParts = (c.numero_comprobante || '').split('-');
      if (nroParts.length === 2) {
          pVta = nroParts[0];
          numComp = nroParts[1];
      } else {
          numComp = c.numero_comprobante?.replace(/\D/g, '') || '1';
      }

      const ptoVenta = padLeft(pVta, 5);
      const nroComprobante = padLeft(numComp, 20);
      
      // CUIT del proveedor: el Libro IVA Compras lo exige. Si falta, queda en ceros.
      const docTipo = '80';
      const cuitProv = String(proveedoresMap[c.proveedor_id] || '').replace(/\D/g, '');
      const docNro = padLeft(cuitProv || '0', 20, '0');
      const provNombre = padRight(c.proveedor_nombre || 'Proveedor', 30);

      const importeTotal = formatAmount(c.total_general);

      // Desglose por alícuota REAL guardado en la compra (RI). Si no existe
      // (compras Monotributo/Exento o previas a la migración), caemos a la
      // aproximación histórica usando el IVA de cabecera.
      const breakdown = Array.isArray(c.iva_breakdown) ? c.iva_breakdown : null;
      let netoGravadoAcum = 0;
      let ivaAcum = 0;
      let exentoAcum = 0;
      let alicEmitidas = 0;

      if (breakdown && breakdown.length > 0) {
        for (const g of breakdown) {
          const pct = Number(g?.percent || 0);
          const base = Number(g?.base || 0);
          const ivaG = Number(g?.iva || 0);
          if (pct === 0) { exentoAcum += base; continue; } // exento: sin alícuota
          if (base <= 0 && ivaG <= 0) continue;
          netoGravadoAcum += base;
          ivaAcum += ivaG;
          const code = String(Number(g?.Id) || afipIvaIdFromPercent(pct)).padStart(4, '0');
          txtAlicuotas += cbteTipo + ptoVenta + nroComprobante + docTipo + docNro + formatAmount(base) + code + formatAmount(ivaG) + "\r\n";
          alicuotasCount++;
          alicEmitidas++;
        }
      } else if (Number(c.iva) > 0) {
        // Fallback: compra sin desglose pero con IVA en cabecera -> 21% (0005).
        netoGravadoAcum = Number(c.subtotal || 0);
        ivaAcum = Number(c.iva || 0);
        txtAlicuotas += cbteTipo + ptoVenta + nroComprobante + docTipo + docNro + formatAmount(c.subtotal) + '0005' + formatAmount(c.iva) + "\r\n";
        alicuotasCount++;
        alicEmitidas++;
      }

      const importeIva = formatAmount(ivaAcum);
      const opExentas = formatAmount(exentoAcum);
      const cantAlic = padLeft(alicEmitidas, 1);
      const codigoOp = alicEmitidas === 0 ? 'E' : ' ';

      // Estructura Libro IVA Compras
      // Fecha(8) + TipoCmp(3) + PtoVta(5) + NroCmp(20) + Despacho(16) + DocTipo(2) + DocNro(20) + NombreProv(30) + Total(15) + NoGrav(15) + Exento(15) + PercepIVA(15) + PercepNac(15) + PercepIB(15) + PercepMuni(15) + ImpInt(15) + Moneda(3) + TipCambio(10) + CantAlicuotas(1) + CodOperacion(1) + CredFiscal(15)...
      const registro =
        fecha +
        cbteTipo +
        ptoVenta +
        nroComprobante +
        padRight('', 16, ' ') + // Despacho de importación
        docTipo +
        docNro +
        provNombre +
        importeTotal +
        padLeft('0', 15) + // No gravado
        opExentas + // OP Exentas
        padLeft('0', 15) + // Perc IVA
        padLeft('0', 15) + // Perc Nac (Ganancias)
        padLeft('0', 15) + // Perc IB
        padLeft('0', 15) + // Perc Muni
        padLeft('0', 15) + // Imp internos
        'PES' +
        '0001000000' +
        cantAlic +
        codigoOp + // Cod operacion ('E' si no hay alícuotas)
        importeIva + // Credito fiscal computable
        padLeft('0', 15) + // Otros tributos
        padLeft('0', 11) + // CUIT Emisor
        padRight('', 30, ' ') + // Nombre Corredor
        padLeft('0', 11); // CUIT Corredor

      txtComprobantes += registro + "\r\n";
    });

    downloadFile(txtComprobantes, `Compras_Comprobantes_${fechaDesde.replace(/-/g,'')}_al_${fechaHasta.replace(/-/g,'')}.txt`);
    
    if (alicuotasCount > 0) {
        setTimeout(() => {
            downloadFile(txtAlicuotas, `Compras_Alicuotas_${fechaDesde.replace(/-/g,'')}_al_${fechaHasta.replace(/-/g,'')}.txt`);
        }, 500);
    }
  };

  const tabs = [
    { id: 'ventas', label: 'Libro IVA Ventas', icon: <Receipt size={16} /> },
    { id: 'compras', label: 'Libro IVA Compras', icon: <Building2 size={16} /> },
    { id: 'ret_sufridas', label: 'Retenciones Sufridas', icon: <ArrowRightLeft size={16} className="text-emerald-500"/> },
    { id: 'ret_practicadas', label: 'Retenciones Practicadas', icon: <ArrowRightLeft size={16} className="text-rose-500" /> }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="text-emerald-600" />
            Reportes Impositivos
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cumplimiento AFIP, Rentas, Libros IVA Digital y Retenciones.
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
          {billingEntities.length > 0 && (
            <>
              <div className="flex items-center gap-2 px-2">
                <Building2 size={16} className="text-slate-400" />
                <select
                  value={emisorSel}
                  onChange={e => setEmisorSel(e.target.value)}
                  className="text-sm border-none bg-transparent outline-none text-slate-700 cursor-pointer max-w-[220px]"
                  title="El Libro IVA Ventas es por CUIT. Elegí el emisor."
                >
                  {billingEntities.length > 1 && <option value="">— Elegí emisor (CUIT) —</option>}
                  {billingEntities.map(be => (
                    <option key={be.id} value={be.id}>
                      {be.tax_name || 'Emisor'}{be.cuit ? ` · ${be.cuit}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-slate-300">|</span>
            </>
          )}
          <div className="flex items-center gap-2 px-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={fechaDesde}
              onChange={e => setFechaDesde(e.target.value)}
              className="text-sm border-none bg-transparent outline-none text-slate-700 cursor-pointer"
            />
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-2 px-2">
            <input 
              type="date" 
              value={fechaHasta}
              onChange={e => setFechaHasta(e.target.value)}
              className="text-sm border-none bg-transparent outline-none text-slate-700 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* TABS NAVEGACIÓN */}
      <div className="flex gap-2 border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative
              ${activeTab === tab.id ? 'text-emerald-600' : 'text-slate-500 hover:text-slate-800'}`}
          >
            {tab.icon}
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-t-full" />
            )}
          </button>
        ))}
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 min-h-[500px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mb-2"></div>
            <p>Calculando reportes fiscales...</p>
          </div>
        ) : (
          <>
            {/* VISTA: VENTAS */}
            {activeTab === 'ventas' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <span className="text-sm text-slate-600 font-medium ml-2">Total de comprobantes: {datosVentas.length}</span>
                  <div className="flex gap-2">
                    <button onClick={generarTxtVentas} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition">
                      <Download size={16} /> Txt AFIP (Comprobantes)
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-100 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                                <th className="p-3 font-semibold rounded-tl-lg">Fecha</th>
                                <th className="p-3 font-semibold">Comprobante</th>
                                <th className="p-3 font-semibold">Cliente</th>
                                <th className="p-3 font-semibold">CUIT/DNI</th>
                                <th className="p-3 font-semibold text-right">Neto Grav.</th>
                                <th className="p-3 font-semibold text-right">IVA Liquidado</th>
                                <th className="p-3 font-semibold text-right rounded-tr-lg">Total</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-100">
                            {datosVentas.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-slate-400">No hay ventas en este período</td></tr>
                            ) : (
                                datosVentas.map(v => {
                                    const esFacturado = v.invoice_status === 'facturado';
                                    // Usamos el neto/IVA reales guardados al emitir el CAE; si la
                                    // venta es previa al snapshot, caemos a la aproximación al 21%.
                                    const netoApprox = v.invoice_imp_neto != null ? Number(v.invoice_imp_neto) : Number(v.total) / 1.21;
                                    const ivaApprox = v.invoice_imp_iva != null ? Number(v.invoice_imp_iva) : (Number(v.total) - netoApprox);

                                    return (
                                    <tr key={v.id} className={`hover:bg-slate-50 ${!esFacturado && 'opacity-60 grayscale'}`}>
                                        <td className="p-3">{new Date(v.fecha_facturacion || v.invoice_sent_at || v.created_at).toLocaleDateString()}</td>
                                        <td className="p-3">
                                            {esFacturado ? (
                                                <span className="font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                                                    {v.invoice_type_label || 'FC'} {v.numero_factura}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">No Facturado</span>
                                            )}
                                        </td>
                                        <td className="p-3 truncate max-w-[200px]">{v.clients?.full_name || 'Consumidor Final'}</td>
                                        <td className="p-3 text-slate-500">{v.clients?.doc_number || '-'}</td>
                                        <td className="p-3 text-right text-slate-600">${esFacturado ? netoApprox.toLocaleString('es-AR', {minimumFractionDigits:2}) : '0.00'}</td>
                                        <td className="p-3 text-right text-slate-600">${esFacturado ? ivaApprox.toLocaleString('es-AR', {minimumFractionDigits:2}) : '0.00'}</td>
                                        <td className="p-3 text-right font-bold text-slate-800">${Number(v.total).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
                                    </tr>
                                )})
                            )}
                        </tbody>
                    </table>
                </div>
              </div>
            )}

            {/* VISTA: COMPRAS */}
            {activeTab === 'compras' && (
              <div className="space-y-4">
                  <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <span className="text-sm text-slate-600 font-medium ml-2">Total de compras ingresadas: {datosCompras.length}</span>
                  <div className="flex gap-2">
                    <button onClick={generarTxtCompras} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition">
                      <Download size={16} /> Txt AFIP (Compras)
                    </button>
                  </div>
                </div>
                {datosCompras.length === 0 ? (
                     <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl mt-4">No hay compras ingresadas en este período</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse mt-2">
                        <thead>
                            <tr className="bg-slate-100 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                                <th className="p-3 font-semibold rounded-tl-lg">Fecha</th>
                                <th className="p-3 font-semibold">Proveedor</th>
                                <th className="p-3 font-semibold">Comprobante</th>
                                <th className="p-3 font-semibold text-right">Neto Gravado</th>
                                <th className="p-3 font-semibold text-right">IVA</th>
                                <th className="p-3 font-semibold text-right rounded-tr-lg">Total General</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-100">
                            {datosCompras.map(c => (
                                <tr key={c.id} className="hover:bg-slate-50">
                                     <td className="p-3">{new Date(c.fecha).toLocaleDateString()}</td>
                                     <td className="p-3 truncate max-w-[200px]">{c.proveedor_nombre}</td>
                                     <td className="p-3">
                                         <span className="font-mono text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-100 text-xs">
                                             {c.tipo_comprobante} {c.numero_comprobante}
                                         </span>
                                     </td>
                                     <td className="p-3 text-right text-slate-600">${Number(c.subtotal).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
                                     <td className="p-3 text-right text-slate-600">${Number(c.iva).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
                                     <td className="p-3 text-right font-bold text-slate-800">${Number(c.total_general).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
                                </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                )}
              </div>
            )}

            {/* VISTA: RETENCIONES SUFRIDAS */}
            {activeTab === 'ret_sufridas' && (
              <div className="space-y-4">
                 <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-600 font-medium ml-2">Retenciones deducidas (Saldos a favor)</span>
                    <button className="text-sm bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50">+ Nueva Retención</button>
                 </div>
                 
                 {retSufridas.length === 0 ? (
                     <div className="p-10 text-center flex flex-col items-center justify-center text-slate-400 border border-dashed border-slate-200 rounded-xl mt-4">
                        <AlertCircle className="mb-2 opacity-50" size={32}/>
                        <p>No tienes retenciones sufridas cargadas.</p>
                        <p className="text-xs mt-1">Asegúrate de haber ejecutado el script SQL para crear la tabla.</p>
                     </div>
                 ) : (
                     <p>Listado WIP...</p>
                 )}
              </div>
            )}

            {/* VISTA: RETENCIONES PRACTICADAS */}
            {activeTab === 'ret_practicadas' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-sm text-slate-600 font-medium ml-2">Retenciones que debés depositar (Agente de Recaudación)</span>
                    <button className="text-sm bg-white border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50">+ Nuevo Certificado</button>
                 </div>

                 {retPracticadas.length === 0 ? (
                     <div className="p-10 text-center flex flex-col items-center justify-center text-slate-400 border border-dashed border-slate-200 rounded-xl mt-4">
                        <AlertCircle className="mb-2 opacity-50" size={32}/>
                        <p>No emitiste certificados de retención.</p>
                     </div>
                 ) : (
                     <p>Listado WIP...</p>
                 )}
              </div>
            )}

          </>
        )}
      </div>

    </div>
  );
}
